// src/pages/FiatExchange/hooks/useCoinBaseOnrampQuote.ts
import { useEffect } from 'react'

import { useQuery, useQueryClient } from '@tanstack/react-query'

import { BuyQuoteRequest } from '@src/types/coinbase'
import { createContextLogger } from '@src/utils'

import { cbGetOnrampQuote } from '../api/coinbase.api'

const logger = createContextLogger('useCoinBaseOnrampQuote')

export const useCoinBaseOnrampQuote = (
    params: BuyQuoteRequest | null,
    options?: {
        isCoinbaseQuoteEnabled?: boolean
        coinbaseQuoteQueryKey?: unknown[]
    }
) => {
    const queryClient = useQueryClient()
    const isEnabled = options?.isCoinbaseQuoteEnabled ?? true
    const queryKey = options?.coinbaseQuoteQueryKey || ['coinbaseQuote', params ?? 'null']

    logger.debug('useCoinBaseOnrampQuote hook initialized with params:', {
        requestParams: params,
        isEnabled,
        queryKey: JSON.stringify(queryKey),
    })

    const query = useQuery({
        queryKey,
        queryFn: async () => {
            if (!params) {
                logger.warn('Cannot fetch quote with null params')
                throw new Error('Quote params are null')
            }

            try {
                logger.info('Fetching Coinbase quote with params:', {
                    purchase_currency: params.purchase_currency,
                    payment_amount: params.payment_amount,
                    payment_currency: params.payment_currency,
                    payment_method: params.payment_method,
                    country: params.country,
                    purchase_network: params.purchase_network,
                })

                const startTime = performance.now()
                const quote = await cbGetOnrampQuote(params)
                const endTime = performance.now()

                logger.success('Coinbase quote fetched successfully in', Math.round(endTime - startTime), 'ms')
                logger.debug('Quote details:', {
                    quote_id: quote.quote_id,
                    payment_total: quote.payment_total,
                    network_fee: quote.network_fee,
                    purchase_amount: quote.purchase_amount,
                })

                return quote
            } catch (error) {
                logger.error('Failed to fetch Coinbase quote:', error)
                throw error
            }
        },
        enabled: isEnabled && !!params,
        refetchOnWindowFocus: false,
        staleTime: 30000, // 30 seconds
    })

    const forceRefetch = async () => {
        if (!params) {
            logger.warn('Cannot force refetch with null params')
            return
        }

        logger.info('Forcing refetch of Coinbase quote for:', {
            purchase_currency: params.purchase_currency,
            payment_currency: params.payment_currency,
            payment_amount: params.payment_amount,
        })
        await queryClient.invalidateQueries({ queryKey })
        logger.debug('Query invalidated, refetch triggered')
    }

    // Log when query status changes
    useEffect(() => {
        if (query.isLoading) {
            logger.debug('Quote query loading state active')
        }
        if (query.isFetching) {
            logger.debug('Quote query fetching state active')
        }
        if (query.isError && query.error) {
            logger.error('Quote query error state active:', query.error)
        }
        if (query.isSuccess && query.data) {
            logger.success('Quote query completed successfully with data')
        }
    }, [query.isLoading, query.isFetching, query.isError, query.isSuccess, query.error, query.data])

    return {
        ...query,
        forceRefetch,
        isFetching: query.isFetching,
        isLoading: query.isLoading,
        error: query.error,
    }
}
